import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntityGoodGovernanaces } from 'app/entities/good-governanace/good-governanace.reducer';
import { Container, Row, Col, Label, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'reactstrap';
import { JhiItemCount, JhiPagination, getSortState } from 'react-jhipster';
import GoodGovernanceUpdate from './good-governance-update';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { MODULE_PARENT_TOPIC_ID } from 'app/shared/common-fun/useModuleTopics';
import TopicFilter, { ITopicFilter, filterByTopics } from 'app/shared/common-fun/TopicFilter';
import TruncatedText from 'app/shared/common-fun/TruncatedText';
import { renderPreview, baseProfileImageUrl } from 'app/shared/common-fun';
import { IGoodGovernanace } from 'app/shared/model/good-governanace.model';

const GoodGovernancePage: React.FC = () => {
  const account = useAppSelector(state => state.authentication.account);
  const id = account.id;
  const dispatch = useAppDispatch();
  const baseImageUrlProfile = baseProfileImageUrl;

  const [governanceData, setGovernanceData] = useState<IGoodGovernanace[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showView, setShowView] = useState('list');
  const [selectedGovernance, setSelectedGovernance] = useState<IGoodGovernanace | null>(null);
  const [filter, setFilter] = useState<ITopicFilter>({ topics: [] });
  // the user's posts that have the chosen topics, so paging them does not ask the server again
  const filteredPosts = useRef<typeof governanceData | null>(null);
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, 20, 'id', 'desc'), location.search)
  );

  const fetchGoodGovernances = async () => {
    const filtering = filter.topics.length > 0;
    const { activePage, itemsPerPage } = paginationState;
    if (filtering && filteredPosts.current) {
      setGovernanceData(filteredPosts.current.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage));
      return;
    }
    setLoading(true);
    try {
      // topics are stored as one text column, so with a topic filter the user's list is fetched whole,
      // filtered here and paged locally
      const result = await dispatch(
        getEntityGoodGovernanaces({
          id,
          page: filtering ? 0 : activePage - 1,
          size: filtering ? 1000 : itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        })
      ).unwrap();
      const data = Array.isArray(result.data) ? result.data : [];
      if (filtering) {
        const filtered = filterByTopics(data, filter.topics);
        filteredPosts.current = filtered;
        setGovernanceData(filtered.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage));
        setTotalItems(filtered.length);
      } else {
        setGovernanceData(data);
        setTotalItems(parseInt(result.headers['x-total-count'], 10) || 0);
      }
    } catch (e) {
      setGovernanceData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    fetchGoodGovernances();
  }, [paginationState, filter]);

  const handleSubmit = async () => {
    filteredPosts.current = null;
    await fetchGoodGovernances();
    setShowView('list');
  };

  const handleFilter = (next: ITopicFilter) => {
    filteredPosts.current = null;
    setFilter(next);
    setPaginationState(prev => ({ ...prev, activePage: 1 }));
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  function formatDate(dateString) {
    const inputDate = new Date(dateString);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    if (inputDate >= currentDate) {
      const options = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      };

      return 'Today ' + inputDate.toLocaleTimeString('en-US', options as Intl.DateTimeFormatOptions);
    } else {
      const options = {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      };

      return inputDate.toLocaleString('en-US', options as Intl.DateTimeFormatOptions);
    }
  }

  return (
    <Container className="inspiration-page card meeting-border">
      {(showView === 'edit' || showView === 'add') && (
        <GoodGovernanceUpdate governance={selectedGovernance} handleSubmit={handleSubmit} showView={showView} />
      )}
      {
        <>
          <Row className="page-header">
            <Col md="6" className="heading">
              <h1>My Good Governance</h1>
            </Col>
            <Col md="6" className="add-btn">
              {showView !== 'add' && (
                <div className="inspiration-add-btn d-flex align-items-center justify-content-center gap-2">
                  <FontAwesomeIcon icon="plus" />
                  <Label
                    className="add-new-btn text-nowrap fs-6"
                    onClick={() => {
                      setSelectedGovernance(null);
                      setShowView('add');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Add new Good Governance
                  </Label>
                </div>
              )}
            </Col>
          </Row>
          <TopicFilter parentId={MODULE_PARENT_TOPIC_ID.governance} onApply={handleFilter} />
          <Row className={`inspiration-list loader-area ${loading && hasLoaded ? 'is-refreshing' : ''}`}>
            {!hasLoaded ? (
              <Spinner className="body-loader-icon" />
            ) : governanceData.length > 0 ? (
              governanceData.map(governance => (
                <Col md="4" key={governance.id} className="d-flex">
                  <div className="inspiration-item approval-card">
                    <TruncatedText className="inspiration-text" text={governance.description} />
                    <TruncatedText className="inspiration-topic" text={governance.topics} lines={2} />
                    <Col md={12} className="inspiration-img">
                      {renderPreview(governance?.imageName)}
                    </Col>
                    <Row className="approval-card-footer">
                      <Col md="12">
                        <div className="inspiration-time">{formatDate(governance.createdDate)}</div>
                      </Col>
                      <div className="inspiration-link">
                        <a href={`${governance.link}`}>{governance.link}</a>
                      </div>
                      <div className="inspiration-profile-details">
                        <div className="inspiration-user-profile">
                          {(governance?.user as any)?.imageUrl ? (
                            <img className="img-thumbnail p-0" src={`${baseImageUrlProfile}/${(governance.user as any).imageUrl}`} />
                          ) : null}
                          <div className="inspiration-user-name">{`${governance?.user?.firstName ?? ''} ${
                            governance?.user?.lastName ?? ''
                          }`}</div>
                        </div>
                        <Button
                          onClick={() => {
                            setShowView('edit');
                            setSelectedGovernance(governance);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          color="primary"
                          size="sm"
                          data-cy="entityEditButton"
                        >
                          <FontAwesomeIcon icon="pencil-alt" /> <span className="d-none d-md-inline"></span>
                        </Button>
                      </div>
                    </Row>
                  </div>
                </Col>
              ))
            ) : filter.topics.length > 0 ? (
              <div className="no-results text-center py-5">No results found for the selected topics.</div>
            ) : (
              <div className="connection-tab-gif">
                <Row>
                  <div className="col-md-6">
                    <p>
                      Here you can share good governance information, such as civic initiatives, transparency practices, or helpful
                      resources for the community. Add your first good governance post to get started!
                    </p>
                  </div>
                  <div className="col-md-6">
                    <img src="content/images/inspiration_no_content.png" />
                  </div>
                </Row>
              </div>
            )}
          </Row>
          {totalItems > 0 && (
            <div className={governanceData.length > 0 ? '' : 'd-none'}>
              <div className="justify-content-center d-flex">
                <JhiItemCount
                  page={paginationState.activePage}
                  total={totalItems}
                  itemsPerPage={paginationState.itemsPerPage}
                  i18nEnabled
                />
              </div>
              <div className="justify-content-center d-flex">
                <JhiPagination
                  activePage={paginationState.activePage}
                  onSelect={handlePagination}
                  maxButtons={5}
                  itemsPerPage={paginationState.itemsPerPage}
                  totalItems={totalItems}
                />
              </div>
            </div>
          )}
        </>
      }
    </Container>
  );
};

export default GoodGovernancePage;
